type OpenMeteoGeocodingResult = {
  admin1?: string;
  admin2?: string;
  country_code?: string;
  name?: string;
};

type OpenMeteoGeocodingResponse = {
  results?: OpenMeteoGeocodingResult[];
};

export type CityStateSuggestion = {
  city: string;
  label: string;
  secondaryLabel: string;
  state: string;
  stateCode: string;
};

const openMeteoGeocodingEndpoint =
  'https://geocoding-api.open-meteo.com/v1/search';

const stateNameToCode = new Map(
  [
    ['Alabama', 'AL'],
    ['Alaska', 'AK'],
    ['Arizona', 'AZ'],
    ['Arkansas', 'AR'],
    ['California', 'CA'],
    ['Colorado', 'CO'],
    ['Connecticut', 'CT'],
    ['Delaware', 'DE'],
    ['District of Columbia', 'DC'],
    ['Florida', 'FL'],
    ['Georgia', 'GA'],
    ['Hawaii', 'HI'],
    ['Idaho', 'ID'],
    ['Illinois', 'IL'],
    ['Indiana', 'IN'],
    ['Iowa', 'IA'],
    ['Kansas', 'KS'],
    ['Kentucky', 'KY'],
    ['Louisiana', 'LA'],
    ['Maine', 'ME'],
    ['Maryland', 'MD'],
    ['Massachusetts', 'MA'],
    ['Michigan', 'MI'],
    ['Minnesota', 'MN'],
    ['Mississippi', 'MS'],
    ['Missouri', 'MO'],
    ['Montana', 'MT'],
    ['Nebraska', 'NE'],
    ['Nevada', 'NV'],
    ['New Hampshire', 'NH'],
    ['New Jersey', 'NJ'],
    ['New Mexico', 'NM'],
    ['New York', 'NY'],
    ['North Carolina', 'NC'],
    ['North Dakota', 'ND'],
    ['Ohio', 'OH'],
    ['Oklahoma', 'OK'],
    ['Oregon', 'OR'],
    ['Pennsylvania', 'PA'],
    ['Rhode Island', 'RI'],
    ['South Carolina', 'SC'],
    ['South Dakota', 'SD'],
    ['Tennessee', 'TN'],
    ['Texas', 'TX'],
    ['Utah', 'UT'],
    ['Vermont', 'VT'],
    ['Virginia', 'VA'],
    ['Washington', 'WA'],
    ['West Virginia', 'WV'],
    ['Wisconsin', 'WI'],
    ['Wyoming', 'WY'],
  ].map(([state, code]) => [state.toLowerCase(), code]),
);
const stateCodeToName = new Map(
  Array.from(stateNameToCode.entries()).map(([state, code]) => [code, state]),
);
const stateCodes = new Set(stateCodeToName.keys());
const stateNamesByLength = Array.from(stateNameToCode.keys()).sort(
  (left, right) => right.length - left.length,
);

function parseCityStateQuery(query: string) {
  const normalizedQuery = query
    .trim()
    .replace(/[,\s]+/g, ' ')
    .replace(/\s+/g, ' ');
  const parts = normalizedQuery.split(' ');
  const lastPart = parts.at(-1)?.toUpperCase();

  if (lastPart && stateCodes.has(lastPart) && parts.length > 1) {
    return {
      cityQuery: parts.slice(0, -1).join(' '),
      stateCode: lastPart,
    };
  }

  const lowerQuery = normalizedQuery.toLowerCase();
  const matchingState = stateNamesByLength.find((stateName) =>
    lowerQuery.endsWith(` ${stateName}`),
  );

  if (matchingState) {
    return {
      cityQuery: normalizedQuery.slice(0, -matchingState.length).trim(),
      stateCode: stateNameToCode.get(matchingState),
    };
  }

  return {
    cityQuery: normalizedQuery,
    stateCode: undefined,
  };
}

function mapGeocodingResult(
  result: OpenMeteoGeocodingResult,
): CityStateSuggestion | null {
  const city = result.name?.trim();
  const state = result.admin1?.trim();

  if (!city || !state || result.country_code !== 'US') {
    return null;
  }

  const stateCode = stateNameToCode.get(state.toLowerCase()) ?? state;
  const label = `${city}, ${stateCode}`;
  const secondaryLabel =
    result.admin2 && result.admin2 !== city
      ? `${result.admin2}, ${state}`
      : state;

  return {
    city,
    label,
    secondaryLabel,
    state,
    stateCode,
  };
}

export async function searchCityStateSuggestions({
  query,
  signal,
}: {
  query: string;
  signal?: AbortSignal;
}) {
  const { cityQuery, stateCode } = parseCityStateQuery(query);

  if (cityQuery.length < 2) {
    return [];
  }

  const params = new URLSearchParams({
    count: stateCode ? '25' : '12',
    countryCode: 'US',
    format: 'json',
    language: 'en',
    name: cityQuery,
  });
  const response = await fetch(`${openMeteoGeocodingEndpoint}?${params}`, {
    signal,
  });

  if (!response.ok) {
    throw new Error('City search is temporarily unavailable.');
  }

  const data = (await response.json()) as OpenMeteoGeocodingResponse;
  const seen = new Set<string>();

  const options = (data.results ?? []).reduce<CityStateSuggestion[]>(
    (mappedOptions, item) => {
      const option = mapGeocodingResult(item);

      if (!option) {
        return mappedOptions;
      }

      const key = `${option.label}-${option.secondaryLabel}`.toLowerCase();

      if (seen.has(key)) {
        return mappedOptions;
      }

      seen.add(key);
      mappedOptions.push(option);

      return mappedOptions;
    },
    [],
  );

  if (!stateCode) {
    return options;
  }

  const stateMatches = options.filter(
    (option) => option.stateCode === stateCode,
  );

  return stateMatches.length > 0 ? stateMatches : options;
}
